import type { Game } from '@game/models/Game';
import { getCurrentPlayer, resetConsecutivePasses } from '@game/models/Game';
import { getCell, updateCell, addPlacedWord } from '@game/models/Board';
import { placeLetterOnCell } from '@game/models/BoardCell';
import type { Letter } from '@game/models/Letter';
import { updateLetterState } from '@game/models/Letter';
import { createPlacedWord } from '@game/models/PlacedWord';
import { removeLettersFromHand, addWordPlayed } from '@game/models/Player';
import { calculateWordScore } from './ScoreCalculator';
import { 
  validatePlacement, 
  getFormedWord,
  getCompleteWordForScoring,
  getCrosswords,
  type PlacedLetterPosition 
} from '@game/rules/PlacementValidator';
import type { Direction, Position } from '@game/types';

export interface WordScore {
  word: string;
  score: number;
}

export interface SubmissionResult {
  success: boolean;
  error?: string;
  word?: string;
  score?: number;
  mainWord?: WordScore;
  crosswords?: WordScore[];
  updatedGame?: Game;
}

/**
 * Determine the direction of a word from positions
 */
function getWordDirection(positions: Position[]): Direction {
  if (positions.length === 1) {
    return 'horizontal'; // Default for single letter
  }
  
  const allSameRow = positions.every(pos => pos.row === positions[0].row);
  return allSameRow ? 'horizontal' : 'vertical';
}

/**
 * Submit a word to the board
 * This is the main entry point for word submission that:
 * 1. Validates placement
 * 2. Calculates score
 * 3. Updates board
 * 4. Updates player
 * 5. Returns result
 */
export function submitWord(
  game: Game,
  placedLetters: PlacedLetterPosition[]
): SubmissionResult {
  // Validate placement
  const validationResult = validatePlacement(placedLetters, game.board);
  
  if (!validationResult.valid) {
    return {
      success: false,
      error: validationResult.error,
    };
  }
  
  const currentPlayer = getCurrentPlayer(game);
  
  // Get the formed word (including existing letters on board)
  const formedWord = getFormedWord(placedLetters, game.board);
  
  // Validate minimum word length (2 letters)
  if (formedWord.length < 2) {
    return {
      success: false,
      error: 'Word must be at least 2 letters long',
    };
  }
  
  // Get ALL positions and characters in the complete word for scoring (including existing board letters)
  const { letterCharacters, positions: allPositions, newlyPlacedPositions } = 
    getCompleteWordForScoring(placedLetters, game.board);
  
  // Get the letters being placed (from player's hand)
  const lettersToPlace: Letter[] = [];
  const letterIds: string[] = [];
  
  for (const pl of placedLetters) {
    const letter = currentPlayer.hand.find(l => l.id === pl.letterId);
    if (letter) {
      lettersToPlace.push(letter);
      letterIds.push(letter.id);
    }
  }
  
  if (lettersToPlace.length === 0) {
    return {
      success: false,
      error: 'No valid letters to place',
    };
  }
  
  // Build complete Letter array for scoring
  // For newly placed positions, use Letter from player's hand
  // For existing positions, get Letter from board
  const allLetters: Letter[] = [];
  console.log('🎯 Building complete word for scoring:');
  console.log('  Formed word:', formedWord);
  console.log('  Letter characters:', letterCharacters);
  console.log('  All positions:', allPositions);
  console.log('  Newly placed positions:', Array.from(newlyPlacedPositions));
  
  for (let i = 0; i < allPositions.length; i++) {
    const position = allPositions[i];
    const posKey = `${position.row},${position.col}`;
    
    if (newlyPlacedPositions.has(posKey)) {
      // Newly placed - find in player's hand
      const placedLetter = placedLetters.find(
        pl => pl.position.row === position.row && pl.position.col === position.col
      );
      if (placedLetter) {
        const letter = currentPlayer.hand.find(l => l.id === placedLetter.letterId);
        if (letter) {
          console.log(`  [${i}] Position ${posKey}: NEW '${letter.character}' (${letter.points} pts)`);
          allLetters.push(letter);
        } else {
          console.error(`  [${i}] Position ${posKey}: NEW letter not found in hand!`);
        }
      } else {
        console.error(`  [${i}] Position ${posKey}: Placed letter not found!`);
      }
    } else {
      // Existing on board - get from board
      const cell = getCell(game.board, position);
      if (cell && cell.letter) {
        console.log(`  [${i}] Position ${posKey}: EXISTING '${cell.letter.character}' (${cell.letter.points} pts)`);
        allLetters.push(cell.letter);
      } else {
        console.error(`  [${i}] Position ${posKey}: Existing letter not found on board!`);
      }
    }
  }
  
  console.log('  Total letters for scoring:', allLetters.length, 'Expected:', allPositions.length);
  console.log('  Letters:', allLetters.map(l => `${l.character}(${l.points})`).join(' + '));
  
  // Verify we found all letters
  if (allLetters.length !== allPositions.length) {
    console.error('❌ Missing letters! Expected', allPositions.length, 'but got', allLetters.length);
    return {
      success: false,
      error: `Internal error: Could not find all letters for word "${formedWord}"`,
    };
  }
  
  // Calculate score using ALL letters in the word (including existing board letters)
  // But only apply bonuses to newly placed letters
  const scoreResult = calculateWordScore(allLetters, allPositions, game.board, newlyPlacedPositions);
  console.log('  Main word score:', scoreResult.totalScore, 'points');
  
  // Calculate crossword scores
  const crosswords = getCrosswords(placedLetters, game.board);
  let crosswordScore = 0;
  const crosswordScores: WordScore[] = [];
  
  if (crosswords.length > 0) {
    console.log(`\n🔀 Found ${crosswords.length} crossword(s):`);
    
    for (const crossword of crosswords) {
      console.log(`\n  Crossword: "${crossword.word}"`);
      console.log(`    Positions:`, crossword.positions);
      console.log(`    Newly placed:`, Array.from(crossword.newlyPlacedPositions));
      
      // Build Letter array for this crossword
      const crosswordLetters: Letter[] = [];
      for (let i = 0; i < crossword.positions.length; i++) {
        const position = crossword.positions[i];
        const posKey = `${position.row},${position.col}`;
        
        if (crossword.newlyPlacedPositions.has(posKey)) {
          // Newly placed - find in player's hand
          const placedLetter = placedLetters.find(
            pl => pl.position.row === position.row && pl.position.col === position.col
          );
          if (placedLetter) {
            const letter = currentPlayer.hand.find(l => l.id === placedLetter.letterId);
            if (letter) {
              crosswordLetters.push(letter);
            }
          }
        } else {
          // Existing letter on board
          const cell = getCell(game.board, position);
          if (cell && cell.letter) {
            crosswordLetters.push(cell.letter);
          }
        }
      }
      
      // Score this crossword
      const crosswordScoreResult = calculateWordScore(
        crosswordLetters,
        crossword.positions,
        game.board,
        crossword.newlyPlacedPositions
      );
      
      console.log(`    Crossword score: ${crosswordScoreResult.totalScore} points`);
      crosswordScore += crosswordScoreResult.totalScore;
      crosswordScores.push({
        word: crossword.word,
        score: crosswordScoreResult.totalScore
      });
    }
    
    console.log(`\n  Total crossword bonus: ${crosswordScore} points`);
  }
  
  const totalScore = scoreResult.totalScore + crosswordScore;
  console.log(`\n  📊 FINAL SCORE: ${scoreResult.totalScore} (main) + ${crosswordScore} (crosswords) = ${totalScore} points\n`);
  
  // Determine direction
  const positions = placedLetters.map(pl => pl.position);
  const direction = getWordDirection(positions);
  
  // Create PlacedWord (using total score including crosswords)
  const placedWord = createPlacedWord(
    formedWord,
    positions,
    direction,
    currentPlayer.id,
    lettersToPlace,
    totalScore,
    game.turnNumber
  );
  
  // Update board: place letters on cells
  let updatedBoard = game.board;
  
  for (let i = 0; i < placedLetters.length; i++) {
    const pl = placedLetters[i];
    const letter = lettersToPlace[i];
    const cell = getCell(updatedBoard, pl.position);
    
    if (!cell) continue;
    
    // Update letter state
    const updatedLetter = updateLetterState(letter, 'on-board', currentPlayer.id);
    
    // Place letter on cell
    const updatedCell = placeLetterOnCell(cell, updatedLetter, currentPlayer.id, placedWord.id);
    
    // Update board with new cell
    updatedBoard = updateCell(updatedBoard, pl.position, updatedCell);
  }
  
  // Add word to board's placed words
  updatedBoard = addPlacedWord(updatedBoard, placedWord);
  
  // Update player: remove letters from hand, add word to history, update score
  let updatedPlayer = removeLettersFromHand(currentPlayer, letterIds);
  updatedPlayer = addWordPlayed(updatedPlayer, placedWord);
  
  // Update game with new board and player
  let updatedGame: Game = {
    ...game,
    board: updatedBoard,
    players: game.players.map(p => p.id === currentPlayer.id ? updatedPlayer : p),
    updatedAt: new Date(),
  };
  
  // Reset consecutive passes for this player (they played a word)
  updatedGame = resetConsecutivePasses(updatedGame, currentPlayer.id);
  
  return {
    success: true,
    word: formedWord,
    score: totalScore,
    mainWord: {
      word: formedWord,
      score: scoreResult.totalScore
    },
    crosswords: crosswordScores,
    updatedGame,
  };
}
